/**
 * Sélecteurs de fond (image/vidéo) et de musique, avec upload vers le bucket
 * `youtube-assets` (pattern illustration-editor) + prévisualisation.
 */

import { useEffect, useRef, useState } from 'react';
import { cn } from '@jeraisonne/ui';
import { Upload, Music, ImageIcon, Film, Loader2 } from 'lucide-react';
import type { YoutubeAssetKind } from '@jeraisonne/db/schemas/youtube';
import { useAuth } from '@/lib/auth';
import { useYoutubeAssets, type YoutubeAssetRow } from '@/lib/admin/youtube-queries';
import { useUploadYoutubeAsset, signedAssetUrl } from '@/lib/admin/youtube-mutations';
import { inputCls } from './ui';

// ── Fond ─────────────────────────────────────────────────────────────────────

interface BackgroundPickerProps {
  selectedId: string | null;
  onSelect: (assetId: string | null) => void;
}

export function BackgroundPicker({ selectedId, onSelect }: BackgroundPickerProps) {
  const images = useYoutubeAssets('background_image');
  const videos = useYoutubeAssets('background_video');
  const assets = [...(images.data ?? []), ...(videos.data ?? [])];

  // Auto-sélectionne le premier fond disponible si aucun n'est choisi → l'aperçu
  // montre immédiatement le fond sans clic (cas « un seul fond uploadé »).
  useEffect(() => {
    if (!selectedId && assets.length > 0) onSelect(assets[0]!.id);
  }, [selectedId, assets, onSelect]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <UploadButton
          kind="background_image"
          label="Image (jpg/png)"
          accept="image/jpeg,image/png,image/webp"
          icon={ImageIcon}
          onUploaded={onSelect}
        />
        <UploadButton
          kind="background_video"
          label="Vidéo (mp4/webm)"
          accept="video/mp4,video/webm"
          icon={Film}
          onUploaded={onSelect}
        />
      </div>
      <AssetGrid
        assets={assets}
        selectedId={selectedId}
        onSelect={onSelect}
        empty="Aucun fond. Uploade une image ou une vidéo."
      />
    </div>
  );
}

// ── Musique ──────────────────────────────────────────────────────────────────

interface MusicPickerProps {
  selectedId: string | null;
  onSelect: (assetId: string | null) => void;
}

export function MusicPicker({ selectedId, onSelect }: MusicPickerProps) {
  const { data: tracks } = useYoutubeAssets('music');
  return (
    <div className="space-y-3">
      <MusicUpload />
      {(tracks ?? []).length === 0 ? (
        <p className="text-ink-400 font-sans text-xs">
          Aucune musique. Uploade un mp3 avec son crédit.
        </p>
      ) : (
        <div className="space-y-2">
          {(tracks ?? []).map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => onSelect(selectedId === t.id ? null : t.id)}
              className={cn(
                'flex w-full items-center gap-3 rounded-xl border p-3 text-left transition-colors active:scale-[.99]',
                selectedId === t.id
                  ? 'border-primary-300 bg-primary-50'
                  : 'border-ink-200 bg-white',
              )}
            >
              <Music className="text-ink-400 h-4 w-4 shrink-0" />
              <span className="min-w-0 flex-1">
                <span className="font-display text-ink-900 block truncate text-sm font-bold">
                  {t.label}
                </span>
                <span className="text-ink-500 block truncate text-xs">
                  {[t.music_artist, t.music_license].filter(Boolean).join(' · ') ||
                    'Crédit non renseigné'}
                </span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function MusicUpload() {
  const { user } = useAuth();
  const upload = useUploadYoutubeAsset();
  const fileRef = useRef<HTMLInputElement>(null);
  const [artist, setArtist] = useState('');
  const [license, setLicense] = useState('');
  const [source, setSource] = useState('');

  return (
    <div className="border-ink-200 space-y-2 rounded-xl border p-3">
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        <input
          className={inputCls}
          placeholder="Artiste"
          value={artist}
          onChange={(e) => setArtist(e.target.value)}
        />
        <input
          className={inputCls}
          placeholder="Licence (ex: CC-BY)"
          value={license}
          onChange={(e) => setLicense(e.target.value)}
        />
        <input
          className={inputCls}
          placeholder="URL source"
          value={source}
          onChange={(e) => setSource(e.target.value)}
        />
      </div>
      <input
        ref={fileRef}
        type="file"
        accept="audio/mpeg,audio/mp3"
        className="hidden"
        onChange={async (e) => {
          const file = e.target.files?.[0];
          if (!file || !user) return;
          await upload.mutateAsync({
            userId: user.id,
            file,
            kind: 'music',
            label: file.name,
            credit: {
              music_artist: artist || null,
              music_license: license || null,
              music_source_url: source || null,
            },
          });
          if (fileRef.current) fileRef.current.value = '';
        }}
      />
      <button
        type="button"
        onClick={() => fileRef.current?.click()}
        disabled={upload.isPending}
        className="border-ink-200 text-ink-700 inline-flex h-10 items-center gap-2 rounded-lg border bg-white px-3 text-sm font-bold transition-colors active:scale-[.98] disabled:opacity-50"
      >
        {upload.isPending ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Upload className="h-4 w-4" />
        )}
        Uploader un mp3
      </button>
      {upload.isError && (
        <p className="text-accent-600 text-xs">{(upload.error as Error).message}</p>
      )}
    </div>
  );
}

// ── Sous-composants partagés ─────────────────────────────────────────────────

function UploadButton({
  kind,
  label,
  accept,
  icon: Icon,
  onUploaded,
}: {
  kind: YoutubeAssetKind;
  label: string;
  accept: string;
  icon: typeof ImageIcon;
  /** Appelé avec l'id du nouvel asset → permet l'auto-sélection. */
  onUploaded?: (assetId: string) => void;
}) {
  const { user } = useAuth();
  const upload = useUploadYoutubeAsset();
  const fileRef = useRef<HTMLInputElement>(null);
  return (
    <>
      <input
        ref={fileRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={async (e) => {
          const file = e.target.files?.[0];
          if (!file || !user) return;
          const asset = await upload.mutateAsync({ userId: user.id, file, kind, label: file.name });
          onUploaded?.(asset.id);
          if (fileRef.current) fileRef.current.value = '';
        }}
      />
      <button
        type="button"
        onClick={() => fileRef.current?.click()}
        disabled={upload.isPending}
        className="border-ink-200 text-ink-700 inline-flex h-10 items-center gap-2 rounded-lg border bg-white px-3 text-sm font-bold transition-colors active:scale-[.98] disabled:opacity-50"
      >
        {upload.isPending ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Icon className="h-4 w-4" />
        )}
        {label}
      </button>
    </>
  );
}

function AssetGrid({
  assets,
  selectedId,
  onSelect,
  empty,
}: {
  assets: YoutubeAssetRow[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  empty: string;
}) {
  if (assets.length === 0) return <p className="text-ink-400 font-sans text-xs">{empty}</p>;
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
      {assets.map((a) => (
        <button
          key={a.id}
          type="button"
          onClick={() => onSelect(selectedId === a.id ? null : a.id)}
          className={cn(
            'group relative aspect-video overflow-hidden rounded-xl border text-center transition-colors active:scale-[.98]',
            selectedId === a.id
              ? 'border-primary-400 ring-primary-200 ring-2'
              : 'border-ink-200 bg-white',
          )}
        >
          <AssetThumb asset={a} />
          {/* Bandeau légende lisible par-dessus la vignette */}
          <span className="absolute inset-x-0 bottom-0 truncate bg-black/55 px-1.5 py-1 text-[10px] font-medium text-white">
            {a.label}
          </span>
          {selectedId === a.id && (
            <span className="bg-primary-500 absolute right-1 top-1 flex h-4 w-4 items-center justify-center rounded-full text-[9px] font-bold text-white">
              ✓
            </span>
          )}
        </button>
      ))}
    </div>
  );
}

/**
 * Vignette d'un asset de fond. Le bucket `youtube-assets` est privé → on génère
 * une URL signée à la volée. Image → <img>, vidéo → <video> (1re frame).
 * Repli sur l'icône tant que l'URL n'est pas prête (ou en cas d'échec).
 */
function AssetThumb({ asset }: { asset: YoutubeAssetRow }) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    setUrl(null);
    setFailed(false);
    signedAssetUrl(asset.bucket_path)
      .then((u) => alive && setUrl(u))
      .catch(() => alive && setFailed(true));
    return () => {
      alive = false;
    };
  }, [asset.bucket_path]);

  const Icon = asset.kind === 'background_video' ? Film : ImageIcon;

  if (failed || !url) {
    return (
      <div className="bg-ink-50 flex h-full w-full items-center justify-center">
        {url === null && !failed ? (
          <Loader2 className="text-ink-300 h-4 w-4 animate-spin" />
        ) : (
          <Icon className="text-ink-300 h-5 w-5" />
        )}
      </div>
    );
  }

  if (asset.kind === 'background_video') {
    return (
      <video
        src={url}
        muted
        playsInline
        preload="metadata"
        className="h-full w-full object-cover"
        onError={() => setFailed(true)}
      />
    );
  }
  return (
    <img
      src={url}
      alt={asset.label}
      className="h-full w-full object-cover"
      onError={() => setFailed(true)}
    />
  );
}
